import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import type { FeatureKey } from "@shared/features";

export type { FeatureKey } from "@shared/features";

/**
 * Resolved override-hierarchy source for a feature flag.
 *
 * Mirrors the server-side `FlagResolvedSource` type
 * (`server/middleware/require-feature.ts`). Indicates which layer of the
 * super-admin → tenant → env-default precedence chain decided the flag's
 * effective value.
 *
 * Owning task: B-1.C.4 / EX-XCUT-0033.
 */
export type FlagResolvedSource = "super-admin" | "tenant" | "env-default";

export interface CatalogFeatureItem {
  key: string;
  label: string;
  description: string;
  domain: string;
  minTier: string;
  dependsOn: string[];
  tierEligible: boolean;
  enabled: boolean;
  /**
   * Which layer of the override hierarchy decided this flag's resolved value.
   * Optional in the type for backward-compat with older fixtures and
   * pre-B-1.C.4 server payloads; the live `/api/tenant/features` endpoint
   * always populates it. Consumers should fall back to `"env-default"`.
   */
  source?: FlagResolvedSource;
  config: Record<string, any>;
  previewExpiresAt: string | null;
  isPreviewing: boolean;
}

interface FeatureFlagsData {
  features: CatalogFeatureItem[];
  tenantTier: string;
}

/**
 * createFlagResolver builds the isEnabled lookup function from a feature list.
 * This is the canonical implementation shared between the useFeatureFlags hook
 * and unit tests — always import this, never re-implement the logic.
 */
export function createFlagResolver(
  features: CatalogFeatureItem[]
): (key: FeatureKey) => boolean {
  const flagMap: Record<string, boolean> = {};
  for (const f of features) {
    flagMap[f.key] = f.enabled;
  }
  return (key: FeatureKey): boolean => {
    if (key in flagMap) return flagMap[key];
    return false;
  };
}

export function useFeatureFlags() {
  const { isAuthenticated, activeTenant } = useAuth();

  const { data, isLoading } = useQuery<FeatureFlagsData>({
    queryKey: ["/api/tenant/features"],
    enabled: isAuthenticated && !!activeTenant,
    // staleTime: 60 s — deliberately below the 5-minute threshold (Task #2496 review).
    // SA flag changes propagate to the tenant UI within ~60 seconds on hard
    // reload; TanStack Query refetches on window-focus so the window is tighter
    // in practice. Reducing further (e.g. 30 s) would increase API pressure
    // with negligible UX benefit given the existing focus-refetch behaviour.
    staleTime: 60_000,
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | null)?.status;
      if (status === 404 || status === 401) {
        if (import.meta.env.DEV) {
          console.warn(
            "[useFeatureFlags] GET /api/tenant/features returned",
            status,
            "— feature flags will default to false for all keys.",
            status === 404
              ? "Ensure the /api/tenant/features endpoint is registered on the server."
              : "",
          );
        }
        return false;
      }
      return failureCount < 3;
    },
  });

  const features = data?.features ?? [];
  const featureMap: Record<string, CatalogFeatureItem> = {};
  for (const f of features) {
    featureMap[f.key] = f;
  }

  const isEnabled = createFlagResolver(features);

  const flagMap: Record<string, boolean> = {};
  for (const f of features) {
    flagMap[f.key] = f.enabled;
  }

  /**
   * Returns the resolved override-hierarchy source for the given key, or
   * `undefined` if the key is unknown / the catalog hasn't loaded yet.
   * Owning task: B-1.C.4 / EX-XCUT-0033.
   */
  const getSource = (key: FeatureKey): FlagResolvedSource | undefined => {
    const f = featureMap[key];
    if (!f) return undefined;
    return f.source ?? "env-default";
  };

  return {
    flags: flagMap,
    featureMap,
    features,
    tenantTier: data?.tenantTier ?? "standard",
    isLoading,
    isEnabled,
    getFeature: (key: string): CatalogFeatureItem | undefined => featureMap[key],
    getSource,
  };
}
